import { groupedVisaOptions, visaTabs } from "./constants";

export const VISA_TAB_ALL = "All";
export const VISA_TAB_OTHERS = "Others";

/** TR is the temporary-residence tab, and for this agency that is the Temporary Graduate Visa. */
const TR_SUBCLASSES = new Set(["485"]);
/** The study stream: the student visa, its guardian visa, and the training visa. */
const STUDENT_SUBCLASSES = new Set(["500", "590", "407"]);

/**
 * Which tab a visa belongs to, decided once per option in {@link groupedVisaOptions}: the two
 * subclass sets above are named explicitly because they cut across the published groupings, and
 * everything else follows the group it is already listed under. Anything left — skilled and
 * permanent-residence visas, refugee and humanitarian visas, crew and resident-return visas — is
 * Others, which is a real answer rather than a hiding place: those visas exist and someone has to
 * be able to find them.
 */
const categoryOfOption = (option) => {
  if (TR_SUBCLASSES.has(option.subclass)) return "TR";
  if (STUDENT_SUBCLASSES.has(option.subclass)) return "Student";
  if (/^bridging visa/i.test(option.name || "")) return "Bridging";
  if (option.group === "Family and Partner Visas") return "Dependent";
  if (option.group === "Visitor Visas") return "Visitor/Tourist";
  return VISA_TAB_OTHERS;
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const CATEGORY_BY_VISA_NAME = groupedVisaOptions.reduce(
  (acc, option) => ({ ...acc, [normalize(option.name)]: categoryOfOption(option) }),
  {}
);

const TAB_VALUES = new Set(
  visaTabs.map((tab) => tab.value).filter((value) => value !== VISA_TAB_ALL)
);

/**
 * The tab one visa application belongs to. `type` is the field the tabs were originally built on,
 * but it is blank on most records — it was only ever filled in by hand — so the visa itself decides
 * when there is nothing typed. A type someone did set always wins: it is a statement about this
 * application, not a guess about its subclass.
 */
export const visaCategoryOf = (application) => {
  const explicit = String(application?.type ?? "").trim();
  if (TAB_VALUES.has(explicit)) return explicit;
  return CATEGORY_BY_VISA_NAME[normalize(application?.visa)] || VISA_TAB_OTHERS;
};

/** True when this application belongs under `tab` ("All" and no tab both mean everything). */
export const matchesVisaTab = (application, tab) =>
  !tab || tab === VISA_TAB_ALL || visaCategoryOf(application) === tab;
